/**
 * Ludo Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SnakesLaddersErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum LudoErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface LudoError {
  type: LudoErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class LudoErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: LudoError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: LudoErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): LudoError {
    const error: LudoError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Ludo', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: LudoErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case LudoErrorType.INVALID_MOVE:
      case LudoErrorType.NOT_YOUR_TURN:
      case LudoErrorType.GAME_ENDED:
      case LudoErrorType.GAME_LOGIC:
      case LudoErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case LudoErrorType.CONNECTION_ERROR:
      case LudoErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case LudoErrorType.CORRUPTED_DATA:
      case LudoErrorType.SAVE_ERROR:
      case LudoErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): LudoError {
    return this.handleError(
      LudoErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleNotYourTurn(): LudoError {
    return this.handleError(
      LudoErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): LudoError {
    return this.handleError(
      LudoErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): LudoError {
    return this.handleError(
      LudoErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): LudoError {
    return this.handleError(
      LudoErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): LudoError {
    return this.handleError(
      LudoErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: LudoErrorType): boolean {
    switch (type) {
      case LudoErrorType.INVALID_MOVE:
      case LudoErrorType.NOT_YOUR_TURN:
      case LudoErrorType.GAME_ENDED:
      case LudoErrorType.GAME_LOGIC:
      case LudoErrorType.CONNECTION_ERROR:
      case LudoErrorType.CORRUPTED_DATA:
      case LudoErrorType.AI_TIMEOUT:
      case LudoErrorType.SAVE_ERROR:
      case LudoErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: LudoError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: LudoError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Ludo Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): LudoError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
